import React from 'react';
import { Input, type InputProps } from '@economic/taco';
import number from '../../../util/utilities/number';

export interface NumberProps extends Omit<InputProps, 'onChange' | 'onBlur'> {
    decimal?: boolean;
    defaultShowZero?: boolean;
    fixedDigits?: number;
    suggestion?: string;
    onChange: (value?: number) => void;
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
}

export function Number({
    decimal = false,
    fixedDigits = 2,
    defaultShowZero = false,
    onChange: handleChange,
    onBlur,
    value,
    suggestion,
    highlighted: externalHighlighted,
    ...props
}: NumberProps) {
    const numberFormatter = decimal ? number.format : String;
    const format = (value: unknown) => {
        if (value || value === 0 || defaultShowZero) {
            if (decimal) {
                return numberFormatter(value || 0, ',', true, fixedDigits);
            }

            return numberFormatter(value || 0);
        }

        return '';
    };

    const parse = decimal ? number.parse : parseInt;
    const [internalValue, setInternalValue] = React.useState<string>(format(value));

    React.useEffect(() => {
        const nextValue = format(value);

        if (internalValue !== nextValue) {
            setInternalValue(nextValue);
        }
    }, [value]);

    const isSuggested = suggestion && value ? format(suggestion) === format(value) : false;
    const highlighted = externalHighlighted || isSuggested;

    return (
        <Input
            {...props}
            highlighted={highlighted}
            onBlur={event => {
                const formattedValue = format(parse(event.target.value));

                setInternalValue(formattedValue);
                const parsedValue = parse(formattedValue);
                handleChange(isNaN(parsedValue ?? NaN) ? undefined : parsedValue);
                event.target.value = formattedValue;

                // since onblur does not bubble, this must be called explicitly
                // must be called to correctly trigger final-form touched/visited state updates used by validation
                onBlur(event);
            }}
            onChange={event => setInternalValue(event.target.value)}
            value={internalValue}
        />
    );
}
